import { useEffect, useMemo, useRef, useState } from 'react';

import type { RosterEmployee } from '@/api/competency-services/profile-assignment/profileAssignmentService';
import type { PaginatedResponse } from '@/types/api';
import type { Competency, CompetencyGroup } from '@/types/competency/Competency';
import type {
    CompetencyProfile,
    CompetencyProfileGroup,
} from '@/types/competency/CompetencyProfile';

import { useGetCompetencyGroups } from '@/api/competency-services/competency';
import {
    useGetCompetencyProfiles,
    useGetCompetencyProfileWithGroups,
} from '@/api/competency-services/competency-profile';
import { isValidProfileId } from '@/api/competency-services/profile-assignment/profileAssignmentService';
import { isCompetencyActive } from '@/utils/competencyHelpers';

const buildCatalogLookup = (groups: CompetencyGroup[]) => {
    const map = new Map<string, { competency: Competency; group: CompetencyGroup }>();
    groups.forEach((group) => {
        group.competencies.forEach((competency) => {
            map.set(competency.id, { competency, group });
        });
    });
    return map;
};

/** The employee's own level on each competency of the selected profile, as recorded or as chosen here. */
export type EmployeeLevels = {
    levelOf: (competencyId: string) => string | null;
    select: (competencyId: string, levelId: string) => void;
};

/** The competencies added to a profile for one employee, and the ones removed from it for them. */
export type CompetencyOverlay = Pick<RosterEmployee, 'addedCompetencyIds' | 'removedCompetencyIds'>;

export type UseProfileSelectorStateProps = {
    /** The employee's own adjustments to `initialProfileId`, which the selection starts from. */
    overlay: CompetencyOverlay;
    initialProfileId?: string;
    /** Whose levels the selector reads and sets; absent, it offers none. */
    levelsEmployeeId?: string;
};

export const useProfileSelectorState = ({
    overlay,
    initialProfileId,
    levelsEmployeeId,
}: UseProfileSelectorStateProps) => {
    const { data: profilesData, isLoading: profilesLoading } = useGetCompetencyProfiles();
    const initialSelectedProfileId = isValidProfileId(initialProfileId) ? initialProfileId : null;

    const [selectedProfileId, setSelectedProfileId] = useState<string | null>(
        initialSelectedProfileId,
    );
    const [search, setSearch] = useState('');
    const [removedIds, setRemovedIds] = useState<Set<string>>(
        () => new Set(initialSelectedProfileId ? (overlay.removedCompetencyIds ?? []) : []),
    );
    const [addedIds, setAddedIds] = useState<string[]>(() =>
        initialSelectedProfileId ? [...(overlay.addedCompetencyIds ?? [])] : [],
    );
    const [showAddModal, setShowAddModal] = useState(false);
    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
    const [chosenLevels, setChosenLevels] = useState<Record<string, string>>({});

    const { data: groupedProfileData, isLoading: groupedProfileLoading } =
        useGetCompetencyProfileWithGroups({
            id: selectedProfileId,
            pageSize: 500,
            enabled: isValidProfileId(selectedProfileId),
            employeeId: levelsEmployeeId,
        });

    const { data: groupsData } = useGetCompetencyGroups(1, 500, addedIds.length > 0);

    // Track if we've done the initial expansion to avoid re-expanding when user collapses all
    const hasInitiallyExpandedRef = useRef(false);

    useEffect(() => {
        if (groupedProfileData && !hasInitiallyExpandedRef.current) {
            setExpandedGroups(new Set(groupedProfileData.items.map((group) => group.id)));
            hasInitiallyExpandedRef.current = true;
        }
    }, [groupedProfileData]);

    const allProfiles = useMemo(() => profilesData ?? [], [profilesData]);
    const profiles = useMemo(
        () => allProfiles.filter((profile) => profile.isActive),
        [allProfiles],
    );
    const groups = useMemo(() => groupsData ?? [], [groupsData]);
    const catalog = useMemo(() => buildCatalogLookup(groups), [groups]);

    const initialProfile: CompetencyProfile | undefined = useMemo(
        () => allProfiles.find((profile) => profile.id === initialProfileId),
        [allProfiles, initialProfileId],
    );

    const selectedProfile: CompetencyProfile | undefined = useMemo(
        () => allProfiles.find((profile) => profile.id === selectedProfileId),
        [allProfiles, selectedProfileId],
    );

    const profileByGroup = useMemo(() => {
        if (!groupedProfileData) {
            return [];
        }
        return groupedProfileData.items
            .map((group) => ({
                group: {
                    id: group.id,
                    name: group.name,
                },
                items: group.competencies
                    .filter((competency) => isCompetencyActive(competency))
                    .map((competency) => ({
                        competency: {
                            id: competency.id,
                            name: competency.name,
                            description: competency.description,
                            status: competency.status,
                            isIndividuallyDeactivated: competency.isIndividuallyDeactivated,
                        },
                        isPriority: competency.isPriority,
                    })),
            }))
            .filter((entry) => entry.items.length > 0);
    }, [groupedProfileData]);

    const profileCompetencyIds = useMemo(() => {
        if (!groupedProfileData) {
            return [];
        }
        return groupedProfileData.items.flatMap((group) =>
            group.competencies.map((competency) => competency.id),
        );
    }, [groupedProfileData]);

    const recordedLevels = useMemo<Record<string, string>>(
        () =>
            Object.fromEntries(
                (groupedProfileData?.items ?? [])
                    .flatMap((group) => group.competencies)
                    .flatMap(({ id, proficiencyLevelId }) =>
                        proficiencyLevelId ? [[id, proficiencyLevelId] as const] : [],
                    ),
            ),
        [groupedProfileData],
    );

    const changedLevels = Object.fromEntries(
        Object.entries(chosenLevels).filter(
            ([competencyId, levelId]) => levelId !== recordedLevels[competencyId],
        ),
    );

    const employeeLevels: EmployeeLevels | undefined = levelsEmployeeId
        ? {
              levelOf: (competencyId) =>
                  chosenLevels[competencyId] ?? recordedLevels[competencyId] ?? null,
              select: (competencyId, levelId) =>
                  setChosenLevels((current) => ({ ...current, [competencyId]: levelId })),
          }
        : undefined;

    const addedRows = useMemo(
        () =>
            addedIds
                .map((id) => catalog.get(id))
                .filter((entry): entry is { competency: Competency; group: CompetencyGroup } =>
                    Boolean(entry),
                )
                .filter((entry) => isCompetencyActive(entry.competency)),
        [addedIds, catalog],
    );

    const addedByGroup = useMemo(() => {
        const byGroup = new Map<string, { group: CompetencyGroup; items: Competency[] }>();
        addedRows.forEach(({ competency, group }) => {
            const existing = byGroup.get(group.id);
            if (existing) {
                existing.items.push(competency);
            } else {
                byGroup.set(group.id, { group, items: [competency] });
            }
        });
        return Array.from(byGroup.values());
    }, [addedRows]);

    const filteredProfiles = useMemo(() => {
        const query = search.trim().toLowerCase();
        if (!query) {
            return profiles;
        }
        return profiles.filter((profile) => {
            const haystack =
                `${profile.name?.en ?? ''} ${profile.description?.en ?? ''}`.toLowerCase();
            return haystack.includes(query);
        });
    }, [profiles, search]);

    const getProfileCounts = (profile: CompetencyProfile) => ({
        activeCount: profile.items.length,
        priorityCount: profile.items.filter((item) => item.isPriority).length,
    });

    const getGroupedProfileCounts = (groupedProfile: PaginatedResponse<CompetencyProfileGroup>) => {
        let activeCount = 0;
        let priorityCount = 0;
        groupedProfile.items.forEach((group) => {
            group.competencies.forEach((competency) => {
                if (isCompetencyActive(competency)) {
                    activeCount++;
                    if (competency.isPriority) {
                        priorityCount++;
                    }
                }
            });
        });
        return { activeCount, priorityCount };
    };

    const selectProfile = (profileId: string) => {
        setSelectedProfileId(profileId);
        setRemovedIds(new Set());
        setAddedIds([]);
        setChosenLevels({});
    };

    const toggleRemoved = (competencyId: string) => {
        setRemovedIds((current) => {
            const next = new Set(current);
            if (next.has(competencyId)) {
                next.delete(competencyId);
            } else {
                next.add(competencyId);
            }
            return next;
        });
    };

    const toggleGroupExpanded = (groupId: string) => {
        setExpandedGroups((prev) => {
            const next = new Set(prev);
            if (next.has(groupId)) {
                next.delete(groupId);
            } else {
                next.add(groupId);
            }
            return next;
        });
    };

    const removeAddedCompetency = (competencyId: string) => {
        setAddedIds((current) => current.filter((id) => id !== competencyId));
    };

    const isLoading = profilesLoading || (selectedProfileId && groupedProfileLoading);

    const selectedProfileCounts = groupedProfileData
        ? getGroupedProfileCounts(groupedProfileData)
        : { activeCount: 0, priorityCount: 0 };

    return {
        // State
        selectedProfileId,
        search,
        setSearch,
        removedIds,
        addedIds,
        setAddedIds,
        showAddModal,
        setShowAddModal,
        expandedGroups,

        // Data
        initialProfile,
        selectedProfile,
        profileByGroup,
        profileCompetencyIds,
        addedRows,
        addedByGroup,
        filteredProfiles,
        selectedProfileCounts,
        isLoading,
        employeeLevels,
        changedLevels,

        // Helpers
        getProfileCounts,

        // Actions
        selectProfile,
        toggleRemoved,
        toggleGroupExpanded,
        removeAddedCompetency,
    };
};
